import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { CategoriesService } from './services/categories.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, filter, map, switchMap, take, tap } from 'rxjs';
import { Category } from '@app/utils/interfaces/categories.interface';
import { AddCategoryComponent } from './add-category/add-category.component';
import { CONFIRM_DELETE_CATEGORIES, DEFAULT_SHOP_ID } from './constants';
import { TitleCasePipe } from '@angular/common';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { UserStateService } from '@app/services/user-state/user-state.service';
import {
  CATEGORY_SETTINGS,
  FAKE_CATEGORIES_QUANTITY,
  CATEGORIES_COLUMNS_CONFIG,
  CATEGORIES_MODAL_OPTIONS,
} from '@app/utils/constants/categories.constants';
import { IconName } from '@app/utils/enums/icon-enum';
import { CategoriesEnumList } from '@app/utils/enums/settings-categories.enum';
import { TablePaginationComponent } from '@app/components/table-pagination/table-pagination.component';
import { TABLE_PAGINATION_DEFAULT_CONFIG } from '@app/utils/constants/table-pagination.constants';
import { TablePaginationConfig } from '@app/utils/interfaces/table-pagination.interface';
import { SnackbarService } from '@app/services/snackbar/snackbar.service';
import { SnackbarStatus } from '@app/utils/enums/snackbar.enum';
import { ConfirmModalComponent } from '@app/components/shared/confirm-modal/confirm-modal.component';
import { InventoryQueryParams } from '@app/utils/interfaces/inventory-item.interface';
import { InventoryService } from '@app/services/inventory/inventory.service';

@Component({
  selector: 'app-settings-categories',
  standalone: true,
  imports: [
    MatTableModule,
    MatIconModule,
    MatMenuModule,
    MatButtonModule,
    MatSelectModule,
    MatFormFieldModule,
    TranslatePipe,
    MatDividerModule,
    IconComponent,
    TablePaginationComponent,
    TitleCasePipe,
  ],
  templateUrl: './settings-categories.component.html',
  styleUrl: './settings-categories.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsCategoriesComponent {
  private readonly categoriesService = inject(CategoriesService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly userState = inject(UserStateService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly inventoryService = inject(InventoryService);

  public readonly displayedColumns: CategoriesEnumList[] = Object.values(CategoriesEnumList);
  public readonly categories = signal<Category[]>([]);
  public readonly totalCount = signal(0);
  public readonly itemsPerPage = signal(CATEGORY_SETTINGS.itemsPerPage);
  public readonly pageSizes = CATEGORY_SETTINGS.pageSizes;
  public readonly currentPage = signal(CATEGORY_SETTINGS.currentPage);
  public readonly isLoading = signal(true);
  public readonly skeletonRows = signal<Partial<Category>[]>(
    Array(FAKE_CATEGORIES_QUANTITY).fill({}),
  );

  public readonly totalPages = computed(() => Math.ceil(this.totalCount() / this.itemsPerPage()));
  public readonly shopId = computed(() => this.userState.userData()?.data.shopId ?? null);
  public readonly columnsConfig = CATEGORIES_COLUMNS_CONFIG;
  public readonly IconName = IconName;

  public settingCategoriesPagination = signal(TABLE_PAGINATION_DEFAULT_CONFIG);

  constructor() {
    effect(() => {
      const id = this.shopId();

      if (id) {
        untracked(() => this.loadCategories());
      }
    });
  }

  private loadCategories(): void {
    const shopId = this.shopId() ?? DEFAULT_SHOP_ID;

    this.isLoading.set(true);

    this.categoriesService
      .getCategories({
        pageNumber: this.currentPage(),
        pageSize: this.itemsPerPage(),
        shopId: shopId,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((data) => {
          this.settingCategoriesPagination.update((prevState) => ({
            ...prevState,
            totalItems: data.data.totalAmount,
          }));

          this.totalCount.set(data.data.totalAmount);

          return data.data.categories;
        }),
        tap(() => this.isLoading.set(false)),
        catchError(() => {
          this.isLoading.set(false);
          this.snackbarService.openSnackbar(
            SnackbarStatus.FAIL,
            'shop-settings.categories.errors.generic-error',
          );
          return EMPTY;
        }),
      )
      .subscribe((response) => {
        this.categories.set(response);
      });
  }

  public handlePageChange(page: number): void {
    this.updatePagination({ currentPage: page });
  }

  public handlePageSizeChange(size: number): void {
    this.updatePagination({ pageSize: size, currentPage: 1 });
  }

  public openAddCategory(): void {
    const dialogRef = this.dialog.open(AddCategoryComponent, {
      ...CATEGORIES_MODAL_OPTIONS,
      data: { mode: 'add' },
    });

    this.handleDialogClose(dialogRef);
  }

  public onEditCategory(category: Category): void {
    const dialogRef = this.dialog.open(AddCategoryComponent, {
      ...CATEGORIES_MODAL_OPTIONS,
      data: { mode: 'edit', category },
    });

    this.handleDialogClose(dialogRef);
  }

  private updatePagination(paginationConfig: Partial<TablePaginationConfig>): void {
    if (paginationConfig.currentPage !== undefined) {
      this.currentPage.set(paginationConfig.currentPage);
    }

    if (paginationConfig.pageSize !== undefined) {
      this.itemsPerPage.set(paginationConfig.pageSize);
    }

    this.settingCategoriesPagination.update((prevState) => ({
      ...prevState,
      ...paginationConfig,
    }));

    this.loadCategories();
  }

  private handleDialogClose(dialogRef: MatDialogRef<AddCategoryComponent>): void {
    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef), filter(Boolean))
      .subscribe(() => this.loadCategories());
  }

  public onDeleteCategory(category: Category): void {
    const params: InventoryQueryParams = {
      categoryId: category.id,
      status: 'Unavailable',
    };
    this.inventoryService
      .getInventoryItems(params)
      .pipe(
        take(1),
        switchMap((response) => {
          const unavailableItems = response?.data.items || [];
          const totalElementsCount = response?.data?.totalAmount || unavailableItems.length;

          const displayedItemsPreview = unavailableItems.slice(0, 5);

          const dynamicModalConfig = {
            ...CONFIRM_DELETE_CATEGORIES,
            data: {
              ...CONFIRM_DELETE_CATEGORIES.data,
              unavailableItems: displayedItemsPreview,
              totalCount: totalElementsCount,
            },
          };

          const dialogRef = this.dialog.open(ConfirmModalComponent, dynamicModalConfig);
          return dialogRef.afterClosed();
        }),
        takeUntilDestroyed(this.destroyRef),
        tap((res) => {
          if (res) {
            this.onDeleteLogic(category);
          }
        }),
      )
      .subscribe();
  }

  private deleteCategory(id: number): void {
    this.categoriesService
      .deleteCategory(id)
      .pipe(
        tap({
          next: () =>
            this.snackbarService.openSnackbar(
              SnackbarStatus.SUCCESS,
              'shop-settings.categories.deleteCategoryModal.deleteCategorySuccessMessage',
            ),
          error: () =>
            this.snackbarService.openSnackbar(
              SnackbarStatus.FAIL,
              'shop-settings.categories.errors.generic-error',
            ),
        }),
        catchError(() => {
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.loadCategories());
  }

  private onDeleteLogic(category: Category): void {
    this.categoriesService
      .getCategoryProductStatuses(category.id)
      .pipe(
        tap((res) => {
          const data = res.data;
          const hasOnlyUnavailableItems =
            data.available === 0 &&
            data.committed === 0 &&
            data.incoming === 0 &&
            data.unavailable > 0;

          const hasNoItems = category.itemsAmount === 0;

          if (!(hasOnlyUnavailableItems || hasNoItems)) {
            this.snackbarService.openSnackbar(
              SnackbarStatus.FAIL,
              'shop-settings.categories.errors.deleteCategoryErrorMessage',
            );
            return;
          }

          this.deleteCategory(category.id);
        }),
        catchError(() => {
          this.snackbarService.openSnackbar(
            SnackbarStatus.FAIL,
            'shop-settings.categories.errors.generic-error',
          );
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
